import { useEffect, useState, type FormEvent } from "react";
import { LIMITS } from "@yay/shared";
import { config } from "../config";
import { YayLogo } from "./YayLogo";

interface JoinOverlayProps {
  initialUsername: string;
  passwordRequired?: boolean;
  /** Show the password half without requiring a value (showcase QA). */
  passwordOptional?: boolean;
  passwordError?: string | null;
  onJoin: (username: string, password?: string) => void;
}

export function JoinOverlay({
  initialUsername,
  passwordRequired = false,
  passwordOptional = false,
  passwordError = null,
  onJoin,
}: JoinOverlayProps) {
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");

  useEffect(() => {
    setUsername(initialUsername);
  }, [initialUsername]);

  const showPassword = passwordRequired || passwordOptional;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed || trimmed.length > config.maxUsernameLength) return;
    if (showPassword && !passwordOptional && !password) return;
    onJoin(trimmed, showPassword && password ? password : undefined);
  }

  return (
    <div className="join-overlay">
      <form className="join-box" onSubmit={handleSubmit}>
        <div className="join-banner" aria-hidden="true">
          <img
            className="join-banner-image"
            src="/brand/join-banner.webp"
            alt=""
          />
          <YayLogo variant="white" className="join-banner-logo" />
        </div>

        <div className="join-body">
          <p className="join-lead">
            Pick a <span className="join-lead-accent">username</span>
            {showPassword ? (
              <>
                {" "}
                and <span className="join-lead-accent">password</span> to join
              </>
            ) : (
              <> to join</>
            )}
          </p>

          <div className="join-fields">
            {showPassword ? (
              <div className="join-split">
                <input
                  className="join-split-user"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  maxLength={config.maxUsernameLength}
                  autoFocus
                  autoComplete="username"
                  aria-label="Username"
                />
                <input
                  className="join-split-pass"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="password"
                  maxLength={LIMITS.MAX_ROOM_PASSWORD}
                  autoComplete="current-password"
                  aria-label="Room password"
                />
              </div>
            ) : (
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                maxLength={config.maxUsernameLength}
                autoFocus
                autoComplete="username"
                aria-label="Username"
              />
            )}
            {passwordError && (
              <p className="join-error" role="alert">
                {passwordError}
              </p>
            )}
            <button type="submit">Join</button>
          </div>
        </div>
      </form>
    </div>
  );
}
